import axios from 'axios';
import i18next from 'i18next';
import moment from 'moment';
import {
  AnyAction,
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
  ThunkDispatch
} from '@reduxjs/toolkit';
import { CommonRootState } from '../combineReducers';
import { CommonState, error, isFetching } from './common';
import { enqueueRequestErrorSnackbar, enqueueSnackbar } from '../actions/enqueueSnackbar';
import { fetchIncompleteGroupedQuestions } from './incompleteGroupedQuestions';
import { IPublishResults, IQuestionnaireStatus, ISectionStatus } from '../interfaces/questionnaireStatus';
import { ModuleId, SubscriptionTypePublicId } from '../enums/Subscription';
import { OrganizationFeature } from '../interfaces/organization';
import { RequestCallbacksParams } from '../types';

const name = 'questionnaireStatus';
const neverPublishedDate = '0001-01-01T00:00:00Z';

const { ESGContractor } = OrganizationFeature;

type Params = {
  organizationId: string;
};

type RequestQuestionnaireStatusParams = {
  organizationId: string;
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>;
  moduleId?: string;
};

const isFoundationESGEligible = ({ mainSubscription, organization }: CommonRootState) => {
  const hasFoundationSubscription =
    mainSubscription.mainSubscription.typePublicId === SubscriptionTypePublicId.ChasFoundation;

  const hasESGFeature = organization.organization?.features.includes(ESGContractor);

  return !!(hasFoundationSubscription && hasESGFeature);
};

const getModuleIdForSocialSustainability = (state: CommonRootState) =>
  isFoundationESGEligible(state) ? ModuleId.SocialSustainability : undefined;

const requestQuestionnaireStatus = async ({
  organizationId,
  dispatch,
  moduleId
}: RequestQuestionnaireStatusParams): Promise<IQuestionnaireStatus> => {
  try {
    const response = await axios.get<IQuestionnaireStatus>(
      `/api/questionnaire/v1/organizations/${organizationId}/questionnaireStatus`,
      {
        params: moduleId && { moduleId }
      }
    );
    return response.data;
  } catch (error) {
    dispatch(enqueueRequestErrorSnackbar());
    throw error;
  }
};

const handleQuestionnaireStatusFulfilled = (
  state: State,
  action: PayloadAction<IQuestionnaireStatus, string, { arg: Params; requestId: string }, never>
) => {
  state.answersToBePublishedCount = action.payload.answersToBePublishedCount;

  state.publishedRequiredAnswerCount = action.payload.publishedRequiredAnswerCount;
  state.requiredQuestionsCount = action.payload.requiredQuestionsCount;
  state.requiredQuestionsWithDraftAnswersCount = action.payload.requiredQuestionsWithDraftAnswersCount;

  state.lastPublishedDate = action.payload.lastPublishedDate;
  state.lastAnsweredDateTimeUtc = action.payload.lastAnsweredDateTimeUtc;
  state.sectionStatuses =
    action.payload.sectionStatuses?.map((sectionStatus: ISectionStatus) => ({
      ...sectionStatus,
      answeredQuestionsCount:
        sectionStatus.answeredQuestionsCount +
        state.answersIncrementQueue.filter(sectionId => sectionId === sectionStatus.sectionId).length
    })) || [];
  state.answersIncrementQueue = [];
  state.isFetching = false;
};

export const fetchQuestionnaireStatus = createAsyncThunk(
  `${name}/fetch`,
  async ({ organizationId }: Params, { dispatch, getState }) => {
    const state = getState() as CommonRootState;
    const moduleId = getModuleIdForSocialSustainability(state);

    return requestQuestionnaireStatus({ organizationId, dispatch, ...(moduleId && { moduleId }) });
  }
);

export const updateQuestionnaireStatus = createAsyncThunk(
  `${name}/update`,
  async ({ organizationId }: Params, { dispatch, getState }) => {
    const state = getState() as CommonRootState;
    const moduleId = getModuleIdForSocialSustainability(state);

    return requestQuestionnaireStatus({ organizationId, dispatch, ...(moduleId && { moduleId }) });
  },
  {
    condition(_, { getState }) {
      const { isFetching } = (getState() as CommonRootState).questionnaireStatus;
      return !isFetching;
    }
  }
);

export const publishAnswers = createAsyncThunk(
  `${name}/publish`,
  async ({ organizationId, afterSuccess, afterError }: Params & RequestCallbacksParams, { dispatch }) => {
    try {
      const response = await axios.post<IPublishResults>(
        `/api/questionnaire/v1/organizations/${organizationId}/publishedQuestions`
      );
      afterSuccess && afterSuccess();
      const { newAnswersPublishedCount } = response.data;
      dispatch(
        enqueueSnackbar({
          message:
            i18next.t('questionnaire:publishSnackbar.published', {
              count: newAnswersPublishedCount,
              defaultValue: '{{count}} question has been published'
            }) || `${newAnswersPublishedCount} question has been published`,
          options: {
            variant: 'success'
          }
        })
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 400) {
        const { data } = error.response;
        if (data && data.key === 'NOT_ANSWERED_VALIDATION_GROUPS' && data.fields && data.fields.NotAnsweredQuestions) {
          const { NotAnsweredQuestions } = data.fields;
          dispatch(fetchIncompleteGroupedQuestions({ organizationId, questionIds: NotAnsweredQuestions }));
        }
      }
      afterError && afterError();
      dispatch(enqueueRequestErrorSnackbar());
      throw error;
    }
  }
);

export type State = CommonState & {
  answersToBePublishedCount: number;
  publishedRequiredAnswerCount: number;
  requiredQuestionsCount: number;
  requiredQuestionsWithDraftAnswersCount: number;
  lastPublishedDate: string | null;
  lastAnsweredDateTimeUtc: string | null;
  answeredQuestionIDs: string[];
  sectionStatuses: ISectionStatus[];
  answersIncrementQueue: string[];
};
const initialState: State = {
  isFetching: false,
  error: null,
  answersToBePublishedCount: 0,
  publishedRequiredAnswerCount: 0,
  requiredQuestionsWithDraftAnswersCount: 0,
  requiredQuestionsCount: 0,
  lastPublishedDate: null,
  lastAnsweredDateTimeUtc: null,
  answeredQuestionIDs: [],
  sectionStatuses: [],
  answersIncrementQueue: []
};

const publish = createSlice({
  name,
  initialState,
  reducers: {
    incrementAnswersToBePublishedCount(state, action) {
      const { questionId, whenAnswered } = action.payload as { questionId: string; whenAnswered?: string };
      if (state.answeredQuestionIDs.includes(questionId)) return;
      if (
        state.lastPublishedDate &&
        state.lastPublishedDate !== neverPublishedDate &&
        whenAnswered &&
        moment(whenAnswered).isAfter(state.lastPublishedDate)
      )
        return;

      state.answeredQuestionIDs.push(questionId);
      state.answersToBePublishedCount = state.answersToBePublishedCount + 1;
    },
    incrementAnsweredQuestionsCount(state, action) {
      const index = state.sectionStatuses.findIndex(sectionStatus => sectionStatus.sectionId === action.payload);
      if (index !== -1) {
        const total = state.sectionStatuses[index].totalQuestionsCount;
        const answered = state.sectionStatuses[index].answeredQuestionsCount;

        if (answered >= total) return;
        state.sectionStatuses[index].answeredQuestionsCount = answered + 1;
      }
    },
    decrementAnsweredQuestionsCount(state, action) {
      const index = state.sectionStatuses.findIndex(sectionStatus => sectionStatus.sectionId === action.payload);
      if (index !== -1) {
        const answered = state.sectionStatuses[index].answeredQuestionsCount;

        if (answered === 0) return;
        state.sectionStatuses[index].answeredQuestionsCount = answered - 1;
      }
    },
    addAnswerToQueue(state, action) {
      state.answersIncrementQueue.push(action.payload);
    }
  },

  extraReducers: builder => {
    builder
      .addCase(fetchQuestionnaireStatus.pending, isFetching)
      .addCase(fetchQuestionnaireStatus.rejected, error)
      .addCase(fetchQuestionnaireStatus.fulfilled, (state, action) => {
        handleQuestionnaireStatusFulfilled(state, action);
      })
      .addCase(updateQuestionnaireStatus.pending, isFetching)
      .addCase(updateQuestionnaireStatus.rejected, error)
      .addCase(updateQuestionnaireStatus.fulfilled, (state, action) => {
        handleQuestionnaireStatusFulfilled(state, action);
      })
      .addCase(publishAnswers.pending, isFetching)
      .addCase(publishAnswers.rejected, error)
      .addCase(publishAnswers.fulfilled, (state, action) => {
        state.answersToBePublishedCount = 0;
        state.answeredQuestionIDs = [];
        state.lastPublishedDate = action.payload.publishDate;
        state.isFetching = false;
      });
  }
});

export const {
  reducer: questionnaireStatusReducer,
  actions: {
    addAnswerToQueue,
    incrementAnswersToBePublishedCount,
    incrementAnsweredQuestionsCount,
    decrementAnsweredQuestionsCount
  }
} = publish;

type QuestionnaireProgressResult = {
  answered: ISectionStatus[];
  inProgress: ISectionStatus[];
  notAnswered: ISectionStatus[];
};

export const selectQuestionnaireProgress = createSelector(
  (state: CommonRootState) => ({
    sectionStatuses: state.questionnaireStatus.sectionStatuses,
    publishedRequiredAnswerCount: state.questionnaireStatus.publishedRequiredAnswerCount,
    requiredQuestionsCount: state.questionnaireStatus.requiredQuestionsCount,
    requiredQuestionsWithDraftAnswersCount: state.questionnaireStatus.requiredQuestionsWithDraftAnswersCount
  }),
  ({
    sectionStatuses,
    publishedRequiredAnswerCount,
    requiredQuestionsCount,
    requiredQuestionsWithDraftAnswersCount
  }) => {
    const result = sectionStatuses.reduce(
      (acc: QuestionnaireProgressResult, sectionStatus: ISectionStatus) => {
        if (sectionStatus.requiredPublishedAnswersCount === sectionStatus.requiredQuestionsCount) {
          acc.answered = [...acc.answered, sectionStatus];
        } else if (sectionStatus.requiredAnswersCount === 0) {
          acc.notAnswered = [...acc.notAnswered, sectionStatus];
        } else {
          acc.inProgress = [...acc.inProgress, sectionStatus];
        }
        return acc;
      },
      {
        answered: [],
        inProgress: [],
        notAnswered: []
      }
    );

    const publishedProgress = (publishedRequiredAnswerCount * 100) / requiredQuestionsCount;
    const draftAnswersProgress = (requiredQuestionsWithDraftAnswersCount * 100) / requiredQuestionsCount;

    return {
      ...result,
      publishedProgress: requiredQuestionsCount === 0 ? 0 : publishedProgress,
      draftAnswersProgress
    };
  }
);
